import React, { useEffect, useState, useMemo } from "react";
import { api, absoluteUrl, formatError } from "@/api/client";
import { Plus, X, Trash, FileText, Printer, WhatsappLogo, EnvelopeSimple, Upload, DownloadSimple, ArrowsClockwise, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import { CustomFieldsInputs } from "@/components/CustomFieldsManager";
import { printHtml, currency, shareWhatsApp, shareEmail } from "@/lib/printDoc";
import { GeneratedWorkOrders } from "@/components/GeneratedWorkOrders";

const TABS = ["Work Orders", "Invoices", "WO Templates"];

export default function Business() {
  const [tab, setTab] = useState("Work Orders");
  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto fade-in" data-testid="business-page">
      <div className="mb-8">
        <div className="text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500">Commerce</div>
        <h1 className="font-display text-3xl font-bold text-neutral-900 mt-1">Sales &amp; Billing</h1>
        <p className="text-sm text-neutral-500 mt-1">Upload Work Orders, raise Proforma / Tax Invoices, and manage reusable Work Order templates.</p>
      </div>

      <div className="border-b border-neutral-200 flex gap-6 mb-6">
        {TABS.map((t) => (
          <button key={t} data-testid={`biz-tab-${t.toLowerCase().replace(/ /g, "-")}`} onClick={() => setTab(t)} className={`pb-3 text-sm font-medium transition-colors duration-200 border-b-2 ${tab === t ? "border-neutral-900 text-neutral-900" : "border-transparent text-neutral-500 hover:text-neutral-900"}`}>{t}</button>
        ))}
      </div>

      {tab === "Work Orders" && <WorkOrdersTab />}
      {tab === "Invoices" && <InvoicesTab />}
      {tab === "WO Templates" && <TemplatesTab />}
    </div>
  );
}

// ================= Work Orders (upload only) =================
function WorkOrdersTab() {
  const [wos, setWos] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const confirm = useConfirm();

  const load = async () => {
    const [w, c] = await Promise.all([api.get("/work-orders"), api.get("/companies")]);
    setWos(w.data); setCompanies(c.data);
  };
  useEffect(() => { load(); }, []);

  const del = async (wo) => {
    const ok = await confirm({ title: `Delete ${wo.wo_number}?`, message: "This Work Order and its attached file will be removed. This cannot be undone.", confirmLabel: "Delete", tone: "danger" });
    if (!ok) return;
    try { await api.delete(`/work-orders/${wo.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatError(err)); }
  };

  const nameOf = (arr, id) => arr.find((x) => x.id === id)?.name || "";

  const shareWA = async (wo) => {
    const company = companies.find((c) => c.id === wo.company_id);
    let text = `Sharing Work Order ${wo.wo_number}${company ? ` from ${company.name}` : ""}. Please find attached.`;
    try {
      const { data } = await api.post("/share/whatsapp-text", { doc_type: "work_order", doc_number: wo.wo_number, company: company?.name || "", party: "", amount: wo.total || 0 });
      if (data?.text) text = data.text;
    } catch {}
    if (wo.pdf_id) text += `\n\nDocument: ${window.location.origin}/api/uploads/pdf/${wo.pdf_id}`;
    shareWhatsApp(text, "");
  };
  const shareMail = (wo) => {
    const company = companies.find((c) => c.id === wo.company_id);
    shareEmail({
      to: "",
      subject: `Work Order ${wo.wo_number}${company ? " · " + company.name : ""}`,
      body: `Please find Work Order ${wo.wo_number}.${wo.pdf_id ? `\n\nDocument: ${window.location.origin}/api/uploads/pdf/${wo.pdf_id}` : ""}\n\nRegards,\n${company?.name || ""}`,
    });
  };
  const openPdf = (wo) => {
    if (!wo.pdf_id) { toast.error("No file attached to this Work Order"); return; }
    window.open(`${window.location.origin}/api/uploads/pdf/${wo.pdf_id}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div>
      <GeneratedWorkOrders />
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div>
          <div className="font-display font-semibold text-neutral-900">Uploaded Files</div>
          <div className="text-xs text-neutral-500">Finalised work orders uploaded as PDF, PPT or DOC.</div>
        </div>
        <button data-testid="upload-wo-btn" onClick={() => setOpen(true)} className="dos-btn dos-btn-primary"><Upload size={14} /> Upload New Work Order</button>
      </div>
      <div className="dos-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-neutral-500 border-b border-neutral-200">
              <th className="px-4 py-3">WO #</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Company</th><th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3">File</th><th className="px-4 py-3 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {wos.map((o) => (
              <tr key={o.id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50" data-testid={`wo-row-${o.id}`}>
                <td className="px-4 py-3 font-mono text-xs">{o.wo_number}</td>
                <td className="px-4 py-3 font-mono text-xs text-neutral-500">{o.date}</td>
                <td className="px-4 py-3">{nameOf(companies, o.company_id) || "—"}</td>
                <td className="px-4 py-3 text-right font-mono">{o.total ? `₹${o.total.toLocaleString()}` : "—"}</td>
                <td className="px-4 py-3">{o.pdf_filename ? <span className="text-xs text-neutral-600 font-mono">{o.pdf_ext?.toUpperCase() || "PDF"}</span> : <span className="text-xs text-neutral-400">—</span>}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button data-testid={`share-wo-wa-${o.id}`} onClick={() => shareWA(o)} title="Share via WhatsApp" className="p-1.5 rounded hover:bg-emerald-50 text-neutral-400 hover:text-emerald-600"><WhatsappLogo size={14} /></button>
                  <button data-testid={`share-wo-mail-${o.id}`} onClick={() => shareMail(o)} title="Share via Email" className="p-1.5 rounded hover:bg-neutral-100"><EnvelopeSimple size={14} /></button>
                  <button data-testid={`view-wo-${o.id}`} onClick={() => openPdf(o)} title="Open / Print" className="p-1.5 rounded hover:bg-neutral-100"><Printer size={14} /></button>
                  <button data-testid={`del-wo-${o.id}`} onClick={() => del(o)} title="Delete" className="p-1.5 rounded hover:bg-red-50 text-neutral-400 hover:text-red-500"><Trash size={14} /></button>
                </td>
              </tr>
            ))}
            {wos.length === 0 && <tr><td colSpan={6} className="p-12 text-center text-neutral-500 text-sm">No work orders yet. Click <b>Upload New Work Order</b> to add one.</td></tr>}
          </tbody>
        </table>
      </div>
      {open && <WOUploadModal companies={companies} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function WOUploadModal({ companies, onClose, onSaved }) {
  const [form, setForm] = useState({ company_id: companies[0]?.id || "", date: new Date().toISOString().slice(0, 10), wo_number: "", total: "", notes: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Please pick a PDF / PPT / DOC to upload"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v || ""));
      await api.post("/work-orders/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Work Order uploaded"); onSaved();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Upload New Work Order" onClose={onClose} onSubmit={save} testId="wo-upload-modal" submitLabel={saving ? "Uploading…" : "Upload Work Order"} submitTestId="wo-upload-save">
      <div className="border-2 border-dashed border-neutral-300 rounded-md p-4 bg-neutral-50">
        <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1">Work Order File</div>
        <div className="text-[11px] text-neutral-500 mb-2">Upload the finalised Work Order in PDF, PPT or DOC.</div>
        <input data-testid="wo-file" required type="file" accept=".pdf,.ppt,.pptx,.doc,.docx,application/pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/vnd.ms-powerpoint" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-xs" />
        {file && <div className="text-[11px] text-neutral-600 mt-1">Selected: <span className="font-mono">{file.name}</span> ({Math.round(file.size / 1024)} KB)</div>}
      </div>
      <Select label="Company" value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} options={companies} />
      <div className="grid grid-cols-3 gap-2">
        <div><label className="text-xs text-neutral-500 mb-1 block">Date</label><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="dos-input" /></div>
        <div><label className="text-xs text-neutral-500 mb-1 block">WO # (optional)</label><input data-testid="wo-number" value={form.wo_number} onChange={(e) => setForm({ ...form, wo_number: e.target.value })} placeholder="Auto-generated" className="dos-input" /></div>
        <div><label className="text-xs text-neutral-500 mb-1 block">Amount</label><input data-testid="wo-total" type="number" step="0.01" value={form.total} onChange={(e) => setForm({ ...form, total: e.target.value })} placeholder="e.g. 50000" className="dos-input" /></div>
      </div>
      <textarea placeholder="Notes / brief scope (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="dos-input" rows={2} />
    </Modal>
  );
}

// ================= Invoices (Proforma → Tax) =================
function monthLabel(key) {
  if (!key || key === "Unknown") return "No date";
  const [y, m] = key.split("-");
  const names = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  return `${names[parseInt(m, 10) - 1] || ""} ${y}`;
}

function InvoicesTab() {
  const [invoices, setInvoices] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [clients, setClients] = useState([]);
  const [workspace, setWorkspace] = useState({});
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [preview, setPreview] = useState(null);
  const [converting, setConverting] = useState(null);
  const confirm = useConfirm();

  const load = async () => {
    const [i, c, cl, ws] = await Promise.all([api.get("/invoices"), api.get("/companies"), api.get("/clients"), api.get("/workspace/settings").catch(() => ({ data: {} }))]);
    setInvoices(i.data); setCompanies(c.data); setClients(cl.data); setWorkspace(ws.data || {});
  };
  useEffect(() => { load(); }, []);

  const del = async (inv) => {
    const ok = await confirm({ title: `Delete ${inv.invoice_number}?`, message: "This invoice will be permanently deleted.", confirmLabel: "Delete", tone: "danger" });
    if (!ok) return;
    try { await api.delete(`/invoices/${inv.id}`); load(); toast.success("Deleted"); }
    catch (err) { toast.error(formatError(err)); }
  };

  // Group by accounting month — a Proforma converted to Tax later files under the
  // conversion month, so this re-buckets automatically once `date` is updated server-side.
  const grouped = useMemo(() => {
    const map = {};
    invoices.forEach((o) => {
      const key = (o.date || "").slice(0, 7) || "Unknown";
      (map[key] = map[key] || []).push(o);
    });
    return Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]));
  }, [invoices]);

  const exportMonthTaxPdf = (monthKey, monthInvoices) => {
    const taxInvs = monthInvoices.filter((o) => !o.is_proforma);
    if (!taxInvs.length) { toast.error("No Tax Invoices in this month yet"); return; }
    const combined = taxInvs.map((o, idx) => {
      const full = { ...o, company: companies.find((c) => c.id === o.company_id), client: clients.find((cl) => cl.id === o.client_id) };
      const html = buildInvoiceHtml(full, workspace);
      return `<div class="section" style="${idx > 0 ? "page-break-before: always;" : ""}">${html}</div>`;
    }).join("");
    printHtml(combined, { title: `Tax Invoices — ${monthLabel(monthKey)}`, pageSize: "A4" });
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button data-testid="new-invoice-btn" onClick={() => setOpen(true)} className="dos-btn dos-btn-primary" disabled={!companies.length || !clients.length}><Plus size={14} /> New Invoice</button>
      </div>
      {(!companies.length || !clients.length) && <div className="dos-card p-4 mb-4 text-sm text-amber-700 bg-amber-50 border-amber-200">Add companies and clients in Master Data first.</div>}
      <div className="dos-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-neutral-500 border-b border-neutral-200">
              <th className="px-4 py-3">Type</th><th className="px-4 py-3">Number</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Client</th><th className="px-4 py-3 text-right">Total</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {grouped.map(([monthKey, monthInvoices]) => (
              <React.Fragment key={monthKey}>
                <tr className="bg-neutral-50 border-b border-neutral-200" data-testid={`inv-month-${monthKey}`}>
                  <td colSpan={7} className="px-4 py-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold uppercase tracking-wider text-neutral-600">{monthLabel(monthKey)} · {monthInvoices.length} invoice{monthInvoices.length !== 1 ? "s" : ""}</span>
                      <button
                        data-testid={`export-month-tax-${monthKey}`}
                        onClick={() => exportMonthTaxPdf(monthKey, monthInvoices)}
                        disabled={!monthInvoices.some((o) => !o.is_proforma)}
                        title="Export all Tax Invoices in this month as one PDF"
                        className="dos-btn dos-btn-secondary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <DownloadSimple size={12} /> Export Tax Invoices (PDF)
                      </button>
                    </div>
                  </td>
                </tr>
                {monthInvoices.map((o) => (
                  <tr key={o.id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50" data-testid={`inv-row-${o.id}`}>
                    <td className="px-4 py-3">
                      {o.is_proforma
                        ? <span className="dos-badge bg-amber-50 text-amber-700">Proforma</span>
                        : <span className="dos-badge bg-emerald-50 text-emerald-700">Tax</span>}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{o.is_proforma ? o.invoice_number : (o.tax_invoice_number || o.invoice_number)}
                      {!o.is_proforma && o.invoice_number && <div className="text-[10px] text-neutral-400 font-mono">Proforma: {o.invoice_number}</div>}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-neutral-500">{o.date}
                      {!o.is_proforma && o.proforma_date && o.proforma_date !== o.date && <div className="text-[10px] text-neutral-400">Raised: {o.proforma_date}</div>}
                    </td>
                    <td className="px-4 py-3">{clients.find((cl) => cl.id === o.client_id)?.name || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono">₹{o.total?.toLocaleString()}</td>
                    <td className="px-4 py-3"><span className="dos-badge bg-neutral-100 text-neutral-700">{o.status}</span></td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button data-testid={`edit-inv-${o.id}`} onClick={() => setEdit(o)} title="Edit invoice" className="p-1.5 rounded hover:bg-neutral-100"><PencilSimple size={14} /></button>
                      {o.is_proforma && (
                        <button data-testid={`convert-inv-${o.id}`} onClick={() => setConverting(o)} title="Convert to Tax Invoice" className="p-1.5 rounded hover:bg-neutral-100"><ArrowsClockwise size={14} /></button>
                      )}
                      <button data-testid={`view-invoice-${o.id}`} onClick={() => setPreview({ ...o, company: companies.find((c) => c.id === o.company_id), client: clients.find((cl) => cl.id === o.client_id) })} className="p-1.5 rounded hover:bg-neutral-100"><FileText size={14} /></button>
                      <button data-testid={`del-inv-${o.id}`} onClick={() => del(o)} title="Delete" className="p-1.5 rounded hover:bg-red-50 text-neutral-400 hover:text-red-500"><Trash size={14} /></button>
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
            {invoices.length === 0 && <tr><td colSpan={7} className="p-12 text-center text-neutral-500 text-sm">No invoices yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {open && <InvoiceModal companies={companies} clients={clients} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {edit && <InvoiceModal record={edit} companies={companies} clients={clients} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {preview && <DocPreview doc={preview} kind="invoice" onClose={() => setPreview(null)} onDeleted={load} />}
      {converting && <ConvertInvoiceModal inv={converting} companies={companies} onClose={() => setConverting(null)} onSaved={() => { setConverting(null); load(); }} />}
    </div>
  );
}

function InvoiceModal({ record, companies, clients, onClose, onSaved }) {
  const isEdit = !!record;
  const [form, setForm] = useState(record ? {
    company_id: record.company_id || "", client_id: record.client_id || "",
    invoice_number: record.invoice_number || "",
    tax_invoice_number: record.tax_invoice_number || "",
    date: record.date || new Date().toISOString().slice(0, 10),
    due_date: record.due_date || "", payment_terms: record.payment_terms || "Net 30",
    cgst_percent: record.cgst_percent ?? 9, sgst_percent: record.sgst_percent ?? 9,
    igst_percent: record.igst_percent ?? 0, tds_percent: record.tds_percent ?? 0,
    notes: record.notes || "",
    items: (record.items && record.items.length) ? record.items : [{ description: "", hsn: "", quantity: 1, unit: "Nos", rate: 0 }],
  } : {
    company_id: companies[0]?.id || "", client_id: clients[0]?.id || "",
    invoice_number: "",
    tax_invoice_number: "",
    date: new Date().toISOString().slice(0, 10), due_date: "",
    cgst_percent: 9, sgst_percent: 9, igst_percent: 0, tds_percent: 0,
    payment_terms: "Net 30", notes: "",
    items: [{ description: "", hsn: "", quantity: 1, unit: "Nos", rate: 0 }],
  });
  const [custom, setCustom] = useState(record?.custom || {});
  const [saving, setSaving] = useState(false);
  const setItem = (i, k, v) => { const items = [...form.items]; items[i] = { ...items[i], [k]: v }; setForm({ ...form, items }); };
  const addItem = () => setForm({ ...form, items: [...form.items, { description: "", hsn: "", quantity: 1, unit: "Nos", rate: 0 }] });
  const rmItem = (i) => setForm({ ...form, items: form.items.filter((_, x) => x !== i) });
  const subtotal = form.items.reduce((s, i) => s + ((parseFloat(i.quantity) || 0) * (parseFloat(i.rate) || 0)), 0);
  const cgst = subtotal * (parseFloat(form.cgst_percent) || 0) / 100;
  const sgst = subtotal * (parseFloat(form.sgst_percent) || 0) / 100;
  const igst = subtotal * (parseFloat(form.igst_percent) || 0) / 100;
  const tds = subtotal * (parseFloat(form.tds_percent) || 0) / 100;
  const total = subtotal + cgst + sgst + igst - tds;
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const items = form.items.map((i) => ({ ...i, quantity: parseFloat(i.quantity) || 0, rate: parseFloat(i.rate) || 0 }));
      const payload = {
        ...form, items,
        cgst_percent: parseFloat(form.cgst_percent) || 0, sgst_percent: parseFloat(form.sgst_percent) || 0,
        igst_percent: parseFloat(form.igst_percent) || 0, tds_percent: parseFloat(form.tds_percent) || 0,
        custom,
      };
      if (isEdit) {
        // For a Tax Invoice, we don't want to overwrite the original Proforma reference (invoice_number).
        // Only the tax_invoice_number should be user-editable at this stage.
        if (!record.is_proforma) {
          delete payload.invoice_number;
        } else {
          delete payload.tax_invoice_number;
        }
        await api.patch(`/invoices/${record.id}`, payload);
        toast.success("Invoice updated");
      } else {
        delete payload.tax_invoice_number;
        await api.post("/invoices", { ...payload, is_proforma: true });
        toast.success("Proforma Invoice created");
      }
      onSaved();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  };
  const title = isEdit
    ? `Edit ${record.is_proforma ? "Proforma" : "Tax"} Invoice`
    : "New Proforma Invoice";
  const submitLabel = saving ? (isEdit ? "Saving…" : "Creating…") : (isEdit ? "Save Changes" : "Create Invoice");
  return (
    <Modal title={title} onClose={onClose} onSubmit={save} testId="invoice-modal" submitLabel={submitLabel} submitTestId="invoice-save">
      {!isEdit && <div className="text-[11px] text-neutral-500 -mt-1 mb-1">Every invoice starts as a <b>Proforma Invoice</b>. Convert to a Tax Invoice from the list once payment is logged.</div>}
      <div className="grid grid-cols-2 gap-2">
        <Select label="Company" value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} options={companies} />
        <Select label="Client" value={form.client_id} onChange={(v) => setForm({ ...form, client_id: v })} options={clients} />
      </div>
      <div className="grid grid-cols-3 gap-2">
        {isEdit && !record.is_proforma ? (
          <div>
            <label className="text-xs text-neutral-500 mb-1 block">Tax Invoice # (editable)</label>
            <input data-testid="inv-tax-number" value={form.tax_invoice_number} onChange={(e) => setForm({ ...form, tax_invoice_number: e.target.value })} className="dos-input" placeholder="e.g. INV-2026-001" />
            {record.invoice_number && <div className="text-[10px] text-neutral-400 font-mono mt-1">Proforma ref: {record.invoice_number}</div>}
          </div>
        ) : (
          <div><label className="text-xs text-neutral-500 mb-1 block">Proforma Invoice # (editable)</label><input data-testid="inv-number" placeholder={isEdit ? "" : "e.g. PI-2026-001 (auto if empty)"} value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} className="dos-input" /></div>
        )}
        <div><label className="text-xs text-neutral-500 mb-1 block">Date</label><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="dos-input" /></div>
        <div><label className="text-xs text-neutral-500 mb-1 block">Due / Payment terms</label><input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} className="dos-input" placeholder="Net 30" /></div>
      </div>
      <div className="border border-neutral-200 rounded-md overflow-hidden">
        <div className="grid grid-cols-12 gap-1 px-2 py-1.5 bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">
          <div className="col-span-4">Description</div><div className="col-span-1">HSN</div><div className="col-span-2">Qty</div><div className="col-span-2">Unit</div><div className="col-span-2">Rate</div><div className="col-span-1"></div>
        </div>
        {form.items.map((it, i) => (
          <div key={i} className="grid grid-cols-12 gap-1 px-2 py-1 border-t border-neutral-100">
            <input required data-testid={`inv-desc-${i}`} placeholder="Description" value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} className="col-span-4 px-2 py-1 text-sm border border-neutral-200 rounded" />
            <input value={it.hsn} onChange={(e) => setItem(i, "hsn", e.target.value)} placeholder="HSN" className="col-span-1 px-2 py-1 text-sm border border-neutral-200 rounded" />
            <input type="number" step="0.01" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} className="col-span-2 px-2 py-1 text-sm border border-neutral-200 rounded" />
            <input value={it.unit} onChange={(e) => setItem(i, "unit", e.target.value)} className="col-span-2 px-2 py-1 text-sm border border-neutral-200 rounded" />
            <input type="number" step="0.01" value={it.rate} onChange={(e) => setItem(i, "rate", e.target.value)} className="col-span-2 px-2 py-1 text-sm border border-neutral-200 rounded" />
            <button type="button" onClick={() => rmItem(i)} className="col-span-1 text-neutral-400 hover:text-red-500"><Trash size={14} /></button>
          </div>
        ))}
        <button type="button" onClick={addItem} data-testid="inv-add-item" className="w-full px-3 py-2 text-xs text-neutral-500 hover:bg-neutral-50 border-t border-neutral-100">+ Add line</button>
      </div>
      <div className="grid grid-cols-4 gap-2 items-end">
        <div><label className="text-xs text-neutral-500">CGST %</label><input type="number" value={form.cgst_percent} onChange={(e) => setForm({ ...form, cgst_percent: e.target.value })} className="dos-input" /></div>
        <div><label className="text-xs text-neutral-500">SGST %</label><input type="number" value={form.sgst_percent} onChange={(e) => setForm({ ...form, sgst_percent: e.target.value })} className="dos-input" /></div>
        <div><label className="text-xs text-neutral-500">IGST %</label><input type="number" value={form.igst_percent} onChange={(e) => setForm({ ...form, igst_percent: e.target.value })} className="dos-input" /></div>
        <div><label className="text-xs text-neutral-500">TDS %</label><input type="number" value={form.tds_percent} onChange={(e) => setForm({ ...form, tds_percent: e.target.value })} className="dos-input" /></div>
      </div>
      <div className="text-right text-sm space-y-0.5">
        <div className="text-neutral-500">Subtotal: <span className="font-mono text-neutral-900">₹{subtotal.toFixed(2)}</span></div>
        <div className="text-neutral-500">CGST + SGST + IGST − TDS: <span className="font-mono text-neutral-900">₹{(cgst + sgst + igst - tds).toFixed(2)}</span></div>
        <div className="font-display text-lg font-bold">Total: ₹{total.toFixed(2)}</div>
      </div>
      <textarea placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="dos-input" rows={2} />
      <CustomFieldsInputs module="invoices" values={custom} onChange={setCustom} />
    </Modal>
  );
}

function ConvertInvoiceModal({ inv, companies, onClose, onSaved }) {
  const comp = companies.find((c) => c.id === inv.company_id);
  const [num, setNum] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post(`/invoices/${inv.id}/convert-to-tax`, { tax_invoice_number: num.trim() });
      toast.success("Converted to Tax Invoice"); onSaved();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Convert Proforma → Tax Invoice" onClose={onClose} onSubmit={save} testId="convert-modal" submitLabel={saving ? "Converting…" : "Convert Now"} submitTestId="convert-save">
      <div className="text-sm text-neutral-700">
        Converting <b>{inv.invoice_number}</b> to a Tax Invoice. It will be accounted under <b>{new Date().toLocaleString("en-IN", { month: "long", year: "numeric" })}</b> (the conversion month), regardless of the original Proforma date. Ensure payment has been logged into the books before proceeding.
      </div>
      <div>
        <label className="text-xs text-neutral-500 mb-1 block">Tax Invoice # (editable)</label>
        <input data-testid="tax-invoice-number" value={num} onChange={(e) => setNum(e.target.value)} placeholder={`Leave empty to auto-generate (prefix ${comp?.invoice_prefix || "INV"})`} className="dos-input" />
      </div>
    </Modal>
  );
}

// ================= WO Templates =================
function TemplatesTab() {
  const [items, setItems] = useState([]);
  const [file, setFile] = useState(null);
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const confirm = useConfirm();

  const load = async () => { try { const { data } = await api.get("/wo-templates"); setItems(data); } catch (err) { toast.error(formatError(err)); } };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Pick a template file first"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("label", label || file.name); fd.append("description", description);
      await api.post("/wo-templates", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Template uploaded"); setFile(null); setLabel(""); setDescription(""); load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setUploading(false); }
  };
  const download = async (t) => {
    window.open(absoluteUrl(`/api/wo-templates/${t.id}/download`), "_blank", "noopener,noreferrer");
  };
  const del = async (t) => {
    const ok = await confirm({ title: `Delete "${t.label}"?`, message: "This template will be removed for everyone.", confirmLabel: "Delete", tone: "danger" });
    if (!ok) return;
    try { await api.delete(`/wo-templates/${t.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div>
      <div className="dos-card p-5 mb-5">
        <div className="font-display font-semibold text-neutral-900 mb-1">Upload Work Order Template</div>
        <div className="text-xs text-neutral-500 mb-3">Upload reusable PPT / DOC / PDF templates. Anyone with permission can download, edit locally, convert to PDF, then upload as a real Work Order in the Work Orders tab.</div>
        <form onSubmit={upload} className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
          <div className="md:col-span-2">
            <label className="text-xs text-neutral-500 mb-1 block">Template Label</label>
            <input data-testid="tpl-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Civil Contractor WO" className="dos-input" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-neutral-500 mb-1 block">Description (optional)</label>
            <input data-testid="tpl-desc" value={description} onChange={(e) => setDescription(e.target.value)} className="dos-input" />
          </div>
          <div className="md:col-span-1">
            <label className="text-xs text-neutral-500 mb-1 block">File</label>
            <input data-testid="tpl-file" required type="file" accept=".ppt,.pptx,.doc,.docx,.pdf,.xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-xs" />
          </div>
          <button data-testid="tpl-upload" type="submit" disabled={uploading} className="dos-btn dos-btn-primary md:col-span-1"><Upload size={14} /> {uploading ? "Uploading…" : "Upload"}</button>
        </form>
      </div>
      <div className="dos-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-neutral-500 border-b border-neutral-200">
              <th className="px-4 py-3">Label</th><th className="px-4 py-3">Format</th><th className="px-4 py-3">Description</th><th className="px-4 py-3">Uploaded By</th><th className="px-4 py-3">Date</th><th className="px-4 py-3 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((t) => (
              <tr key={t.id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50" data-testid={`tpl-row-${t.id}`}>
                <td className="px-4 py-3 font-medium text-neutral-900">{t.label}</td>
                <td className="px-4 py-3 font-mono text-xs text-neutral-500">{(t.ext || "").toUpperCase()}</td>
                <td className="px-4 py-3 text-neutral-600 text-xs">{t.description || "—"}</td>
                <td className="px-4 py-3 text-neutral-600 text-xs">{t.uploaded_by_name || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-neutral-500">{(t.created_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button data-testid={`dl-tpl-${t.id}`} onClick={() => download(t)} title="Download template" className="p-1.5 rounded hover:bg-neutral-100"><DownloadSimple size={14} /></button>
                  <button data-testid={`del-tpl-${t.id}`} onClick={() => del(t)} title="Delete" className="p-1.5 rounded hover:bg-red-50 text-neutral-400 hover:text-red-500"><Trash size={14} /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-12 text-center text-neutral-500 text-sm">No templates yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ================= Doc Preview (Invoice) =================
function Modal({ title, onClose, onSubmit, children, testId, submitLabel = "Save", submitTestId }) {
  return (
    <div data-testid={testId} className="fixed inset-0 z-50 bg-black/30 overflow-y-auto p-4 flex items-start justify-center" onClick={onClose}>
      <form onSubmit={onSubmit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg border border-neutral-200 w-full max-w-3xl my-8 fade-in flex flex-col max-h-[92vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 flex-shrink-0">
          <div className="font-display font-semibold text-neutral-900">{title}</div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-neutral-100"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto flex-1">{children}</div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-neutral-200 bg-neutral-50 flex-shrink-0 sticky bottom-0 rounded-b-lg">
          <button type="button" onClick={onClose} className="dos-btn dos-btn-secondary">Cancel</button>
          <button type="submit" data-testid={submitTestId} className="dos-btn dos-btn-primary">{submitLabel}</button>
        </div>
      </form>
    </div>
  );
}
function Select({ label, value, onChange, options }) {
  return (
    <div>
      <label className="text-xs text-neutral-500 mb-1 block">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="dos-input" required>
        {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
      </select>
    </div>
  );
}

function buildInvoiceHtml(doc, workspace) {
  const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
  const letterhead = workspace?.letterhead_url || "";
  const isProforma = !!doc.is_proforma;
  const title = isProforma ? "Proforma Invoice" : "Tax Invoice";
  const number = isProforma ? doc.invoice_number : (doc.tax_invoice_number || doc.invoice_number);
  const rows = (doc.items || []).map((it, i) => `
    <tr><td>${i + 1}</td><td>${esc(it.description || "")}</td><td class="font-mono text-xs">${esc(it.hsn || "")}</td>
    <td class="num font-mono">${it.quantity ?? ""}</td><td>${esc(it.unit || "")}</td>
    <td class="num font-mono">${currency(it.rate)}</td><td class="num font-mono">${currency(it.amount)}</td></tr>`).join("");
  const letterheadHtml = letterhead
    ? `<div class="section" style="text-align:center; margin-bottom:8px;"><img class="letterhead" src="${absoluteUrl(letterhead)}" alt=""/></div>`
    : "";
  const logoUrl = !letterhead ? (workspace?.logo_url || "") : ""; // skip the small logo when a full custom letterhead banner is already set
  return `
    ${letterheadHtml}
    <div class="section" style="display:flex; align-items:flex-start; gap:16px; padding-bottom:12px; border-bottom:1px solid #e5e5e5; margin-bottom:16px;">
      <div style="flex:1; display:flex; align-items:flex-start; gap:10px;">
        ${logoUrl ? `<img src="${absoluteUrl(logoUrl)}" alt="" style="width:44px;height:44px;object-fit:contain;border-radius:4px;flex-shrink:0;"/>` : ""}
        <div>
          <div class="font-bold text-xl">${esc(doc.company?.name || "")}</div>
          <div class="text-xs text-neutral-500 whitespace-pre-line">${esc(doc.company?.address || "")}</div>
          <div class="text-xs text-neutral-500 mt-2">GSTIN: ${esc(doc.company?.gst || "—")} · PAN: ${esc(doc.company?.pan || "—")}</div>
        </div>
      </div>
      <div style="text-align:right;">
        <div class="text-xs uppercase tracking-wider text-neutral-500">${title}</div>
        <div class="font-mono text-sm font-bold">${esc(number || "")}</div>
        <div class="text-xs text-neutral-500 mt-1">Date: ${esc(doc.date || "")}</div>
        ${doc.due_date ? `<div class="text-xs text-neutral-500">Due: ${esc(doc.due_date)}</div>` : ""}
        ${!isProforma && doc.invoice_number ? `<div class="text-[10px] text-neutral-400 mt-1">Proforma ref: ${esc(doc.invoice_number)}</div>` : ""}
        ${!isProforma && doc.proforma_date && doc.proforma_date !== doc.date ? `<div class="text-[10px] text-neutral-400">Proforma raised: ${esc(doc.proforma_date)}</div>` : ""}
      </div>
    </div>
    <div class="section" style="display:grid; grid-template-columns:1fr 1fr; gap:24px; margin-bottom:16px;">
      <div><div class="text-xs uppercase tracking-wider text-neutral-500">Bill To</div>
        <div class="font-bold" style="margin-top:4px;">${esc(doc.client?.name || "")}</div>
        <div class="text-xs text-neutral-600 whitespace-pre-line">${esc(doc.client?.address || "")}</div>
        <div class="text-xs text-neutral-500 mt-2">GSTIN: ${esc(doc.client?.gst || "—")}</div>
      </div>
    </div>
    <table class="items section" style="width:100%;">
      <thead><tr><th style="width:32px;">#</th><th>Description</th><th>HSN</th><th class="num" style="width:64px;">Qty</th><th style="width:60px;">Unit</th><th class="num" style="width:100px;">Rate</th><th class="num" style="width:120px;">Amount</th></tr></thead>
      <tbody>${rows || `<tr><td colspan="7" style="text-align:center; color:#a3a3a3; padding:20px;">No items</td></tr>`}</tbody>
    </table>
    <div class="totals section" style="margin-top:16px;">
      <div class="row"><span class="text-neutral-500">Subtotal</span><span class="font-mono">${currency(doc.subtotal)}</span></div>
      <div class="row"><span class="text-neutral-500">CGST (${doc.cgst_percent}%)</span><span class="font-mono">${currency(doc.cgst)}</span></div>
      <div class="row"><span class="text-neutral-500">SGST (${doc.sgst_percent}%)</span><span class="font-mono">${currency(doc.sgst)}</span></div>
      ${(doc.igst || 0) > 0 ? `<div class="row"><span class="text-neutral-500">IGST (${doc.igst_percent}%)</span><span class="font-mono">${currency(doc.igst)}</span></div>` : ""}
      ${(doc.tds || 0) > 0 ? `<div class="row"><span class="text-neutral-500">TDS (${doc.tds_percent}%)</span><span class="font-mono">-${currency(doc.tds)}</span></div>` : ""}
      <div class="row grand"><span>Total</span><span class="font-mono">${currency(doc.total)}</span></div>
    </div>
    ${doc.company?.bank_account ? `<div class="section" style="margin-top:24px; padding-top:12px; border-top:1px solid #e5e5e5; font-size:11px; color:#525252;">
      <div class="font-semibold text-neutral-700" style="margin-bottom:4px;">Bank Details</div>
      <div>${esc(doc.company.bank_name || "")} · A/c: ${esc(doc.company.bank_account || "")} · IFSC: ${esc(doc.company.bank_ifsc || "")}${doc.company.bank_branch ? ` · ${esc(doc.company.bank_branch)}` : ""}</div>
    </div>` : ""}
    ${doc.payment_terms || doc.notes ? `<div class="section" style="margin-top:16px; font-size:11px; color:#525252;">
      ${doc.payment_terms ? `<div><b>Payment terms:</b> ${esc(doc.payment_terms)}</div>` : ""}
      ${doc.notes ? `<div class="whitespace-pre-line" style="margin-top:4px;"><b>Notes:</b> ${esc(doc.notes)}</div>` : ""}
    </div>` : ""}
    ${isProforma ? `<div class="section" style="margin-top:16px; padding:8px 12px; background:#fef3c7; border-left:3px solid #d97706; font-size:11px; color:#78350f;"><b>Proforma:</b> This is a Proforma invoice for advance / booking. It is NOT a valid tax invoice. A separate Tax Invoice will be issued once payment is received.</div>` : ""}
    <div class="section" style="margin-top:24px; text-align:center; font-size:10px; color:#737373; border-top:1px dashed #d4d4d4; padding-top:8px;">
      This is a computer generated invoice and does not need a signature.
    </div>
  `;
}

function DocPreview({ doc, kind, onClose, onDeleted }) {
  const [workspace, setWorkspace] = useState({});
  const [pageSize, setPageSize] = useState("A4");
  const confirm = useConfirm();
  useEffect(() => { api.get("/workspace/settings").then((r) => setWorkspace(r.data)).catch(() => {}); }, []);
  const html = buildInvoiceHtml(doc, workspace);
  const number = doc.is_proforma ? doc.invoice_number : (doc.tax_invoice_number || doc.invoice_number);
  const doPrint = () => printHtml(html, { title: `${doc.is_proforma ? "Proforma" : "Tax Invoice"} ${number}`, pageSize });
  const doWhatsApp = async () => {
    let text = `Sharing ${doc.is_proforma ? "Proforma Invoice" : "Tax Invoice"} ${number} — ${doc.company?.name || ""}. Amount: \u20B9${(doc.total || 0).toLocaleString()}. Please review.`;
    try {
      const { data } = await api.post("/share/whatsapp-text", { doc_type: doc.is_proforma ? "proforma" : "invoice", doc_number: number, company: doc.company?.name || "", party: doc.client?.name || "", amount: doc.total || 0 });
      if (data?.text) text = data.text;
    } catch {}
    shareWhatsApp(text, doc.client?.phone || "");
  };
  const doEmail = () => shareEmail({
    to: doc.client?.email || "",
    subject: `${doc.is_proforma ? "Proforma Invoice" : "Tax Invoice"} ${number} · ${doc.company?.name || ""}`,
    body: `Dear ${doc.client?.name || ""},\n\nPlease find attached ${doc.is_proforma ? "Proforma Invoice" : "Tax Invoice"} ${number} for a total of \u20B9${(doc.total || 0).toLocaleString()}.\n\nRegards,\n${doc.company?.name || ""}`,
  });
  const doDelete = async () => {
    const ok = await confirm({ title: `Delete ${number}?`, message: "This is permanent.", confirmLabel: "Delete", tone: "danger" });
    if (!ok) return;
    try { await api.delete(`/invoices/${doc.id}`); onDeleted?.(); onClose(); toast.success("Deleted"); }
    catch (err) { toast.error(formatError(err)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 overflow-y-auto p-4 flex items-start justify-center" onClick={onClose}>
      <div className="bg-white rounded-lg border border-neutral-200 w-full max-w-3xl my-8 fade-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-200 gap-3 flex-wrap">
          <div className="font-display font-semibold text-neutral-900">{doc.is_proforma ? "Proforma Invoice" : "Tax Invoice"}</div>
          <div className="flex gap-2 items-center flex-wrap">
            <select data-testid="doc-page-size" value={pageSize} onChange={(e) => setPageSize(e.target.value)} className="dos-input py-1 text-xs" style={{ width: 92 }}>
              <option>A4</option><option>Legal</option><option>Letter</option><option>A5</option>
            </select>
            <button data-testid="share-whatsapp" onClick={doWhatsApp} className="dos-btn dos-btn-secondary text-xs"><WhatsappLogo size={14} weight="fill" className="text-emerald-600" /> WhatsApp</button>
            <button data-testid="share-email" onClick={doEmail} className="dos-btn dos-btn-secondary text-xs"><EnvelopeSimple size={14} /> Email</button>
            <button data-testid="print-doc" onClick={doPrint} className="dos-btn dos-btn-primary"><Printer size={14} /> Print / PDF</button>
            <button data-testid="delete-doc" onClick={doDelete} className="dos-btn dos-btn-secondary text-xs text-red-600"><Trash size={14} /></button>
            <button onClick={onClose} className="dos-btn dos-btn-secondary">Close</button>
          </div>
        </div>
        <div className="p-8 max-h-[75vh] overflow-y-auto">
          <div className="doc-preview bg-white border border-neutral-200 rounded-md p-6" dangerouslySetInnerHTML={{ __html: html }} />
        </div>
      </div>
    </div>
  );
}
